
import { useState, useEffect } from "react";
import { Roadmap } from "@/types";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Plus, Save } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

type RoadmapStatusManagementProps = {
  roadmap: Roadmap;
  companyStatuses: any[];
  onClose: () => void;
};

const RoadmapStatusManagement = ({ roadmap, companyStatuses, onClose }: RoadmapStatusManagementProps) => {
  const [selectedStatusIds, setSelectedStatusIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize with roadmap's current statuses
  useEffect(() => {
    if (companyStatuses.length > 0 && roadmap.statuses) {
      // For each company status, check if it's included in the roadmap statuses
      const selectedIds = companyStatuses
        .filter(companyStatus => {
          return roadmap.statuses.some(roadmapStatus => {
            // Compare by name since they're from different tables
            return roadmapStatus.name.toLowerCase() === companyStatus.name.toLowerCase();
          });
        })
        .map(status => status.id);
      
      setSelectedStatusIds(selectedIds);
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  }, [roadmap, companyStatuses]);

  const handleStatusToggle = (statusId: string) => {
    setSelectedStatusIds(prev => 
      prev.includes(statusId)
        ? prev.filter(id => id !== statusId)
        : [...prev, statusId]
    );
  };

  const handleSave = async () => {
    try {
      if (selectedStatusIds.length === 0) {
        toast.error("Please select at least one status");
        return;
      }

      // Get the statuses details
      const selectedStatuses = companyStatuses.filter(status => 
        selectedStatusIds.includes(status.id)
      );

      // First, delete existing roadmap statuses
      const { error: deleteError } = await supabase
        .from('roadmap_statuses')
        .delete()
        .eq('roadmap_id', roadmap.id);
      
      if (deleteError) {
        console.error('Error deleting roadmap statuses:', deleteError);
        toast.error('Failed to update roadmap statuses');
        return;
      }

      // Then, insert the selected statuses as roadmap statuses
      const statusInserts = selectedStatuses.map((status, index) => ({
        roadmap_id: roadmap.id,
        name: status.name,
        color: status.color,
        description: status.description,
        position: index + 1
      }));

      const { error: insertError } = await supabase
        .from('roadmap_statuses')
        .insert(statusInserts);
      
      if (insertError) {
        console.error('Error inserting roadmap statuses:', insertError);
        toast.error('Failed to update roadmap statuses');
        return;
      }

      toast.success('Roadmap statuses updated successfully');
      onClose();
    } catch (error) {
      console.error('Error in handleSave:', error);
      toast.error('Failed to update roadmap statuses');
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading statuses...</div>;
  }

  return (
    <div className="space-y-6 py-4">
      <div className="space-y-4">
        <h3 className="font-medium">Select Statuses for "{roadmap.name}"</h3>
        
        {companyStatuses.length === 0 ? (
          <div className="text-muted-foreground">
            No company statuses found. Please create statuses in the Statuses tab first.
          </div>
        ) : (
          <div className="space-y-2">
            {companyStatuses.map((status) => (
              <div key={status.id} className="flex items-center p-3 border rounded-md">
                <Checkbox 
                  id={`status-${status.id}`} 
                  checked={selectedStatusIds.includes(status.id)}
                  onCheckedChange={() => handleStatusToggle(status.id)}
                />
                <div className="ml-3 flex items-center gap-2">
                  <div className="w-5 h-5 rounded" style={{ backgroundColor: status.color }} />
                  <Label htmlFor={`status-${status.id}`}>
                    {status.name}
                    {status.description && (
                      <span className="text-xs text-muted-foreground ml-2">
                        {status.description}
                      </span>
                    )}
                  </Label>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSave}>
          <Save className="h-4 w-4 mr-2" />
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default RoadmapStatusManagement;
